function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { tokens, assignTokens, BadgeConfig, BasicTableCellConfig, BasicTableHeaderCellConfig, LinkConfig, IconConfig, BackdropConfig, css, TextAreaConfig, SelectConfig, NativeTextFieldConfig, DialogConfig, FormFieldConfig, FormFieldGroupConfig, SwitchConfig, SelectFieldConfig, PhoneNumberFieldConfig, BannerConfig, ToggleItemConfig } from '@sail/ui';
import { dynamic } from '@sail/engine';
import { calculateEmphasizedFontWeight, calculateSubduedFontWeight, getLineHeight, scaleTokenNumber } from './tokenUtils';
import { getColorPrimaryButtonIcon, getColorSecondaryButtonIcon, getColorPrimaryLinkIcon, getColorSecondaryLinkIcon, getActionPrimaryColorTextHover, getColorPrimaryLinkIconHovered, getColorFormTextHover, getColorFormPlaceholder, getColorFormDisabledBackground, getColorFormDisabledText, getColorFormText, getShadowMedium, getButtonPrimaryColorBackgroundPressed, getButtonPrimaryColorBorderHovered, getButtonPrimaryColorBorderPressed, getButtonSecondaryColorBackgroundPressed, getButtonSecondaryColorBorderHovered, getFocusRingColor, getColorFormDisabledBackgroundAccent, getColorOnFormDisabledBackgroundAccent, getActionSecondaryColorTextHovered, getActionSecondaryTextDecorationColorHovered, getActionPrimaryTextDecorationColorHovered, mobileSdkFocusRing, getMobileSdkInputFocusRingOverride as getMobileSdkFocusRingOverride } from './tokenOverrides';
import { assertUnreachable } from './assertUnreachable';
import { getAppearanceValuesWithDefaults } from './getAppearanceValuesWithDefaults';
function assignTokensIfDefined(input, assignment) {
    if (!assignment) {
        return undefined;
    }
    return assignTokens(input, assignment);
}
function addIfDefined(element, arr) {
    if (!element) {
        return;
    }
    arr.push(element);
}
export const flattenThemingIntents = (themingIntents)=>{
    const arr = [];
    themingIntents.forEach((overrideIntent)=>{
        // If there is a token to value pair, assign it if defined
        if (overrideIntent.length !== 1) {
            const tokenOverride = assignTokensIfDefined(overrideIntent[1], overrideIntent[0]);
            addIfDefined(tokenOverride, arr);
        } else {
            // If an Intent value is passed in, assign it if defined
            addIfDefined(overrideIntent[0], arr);
        }
    });
    return arr;
};
/**
 * Layer 2 (direct and derived values) -> Layer 3 mapping: We map Connect JS options to the sail-next tokens that they determine
 * For example theme.buttonPrimaryColorBackground determines the sail-next token color.background.action.primary
 *
 * This function does not have type safety because the mapping between layer 2 and layer 3 is not 1:1 (a single value can map to multiple Sail tokens),
 * please make sure that any new appearance options are also added to the mapping below
 */ export const getTokenOverrideIntents = ({ variables: theme }, shouldThemeForMobileSdk)=>{
    const { font, color, border, space, shadow, focus } = tokens;
    const useMobileSdkTheme = !!shouldThemeForMobileSdk;
    return [
        // Font Family
        [
            theme.fontFamily,
            font.family.typeface
        ],
        // Focus Ring
        [
            useMobileSdkTheme ? mobileSdkFocusRing : getFocusRingColor(theme),
            focus
        ],
        // Primary Button
        [
            theme.buttonPrimaryColorBackground,
            color.background.action.primary
        ],
        [
            theme.buttonPrimaryColorBorder,
            color.border.action.primary
        ],
        [
            theme.buttonPrimaryColorText,
            color.text.onAction.primary
        ],
        [
            theme.buttonPrimaryColorText,
            color.text.onAction.primary.hovered
        ],
        [
            getColorPrimaryButtonIcon(theme),
            color.icon.onAction.primary
        ],
        [
            getColorPrimaryButtonIcon(theme),
            color.icon.onAction.primary.pressed
        ],
        [
            getColorPrimaryButtonIcon(theme),
            color.icon.onAction.primary.hovered
        ],
        [
            getButtonPrimaryColorBackgroundPressed(theme),
            color.background.action.primary.pressed
        ],
        [
            getButtonPrimaryColorBackgroundPressed(theme),
            color.background.action.primary.hovered
        ],
        [
            getButtonPrimaryColorBorderHovered(theme),
            color.border.action.primary.hovered
        ],
        [
            getButtonPrimaryColorBorderPressed(theme),
            color.border.action.primary.pressed
        ],
        // sail-next button text color changes on press
        // keep button text the same on press to preserve accessible button text generation
        [
            theme.buttonPrimaryColorText,
            color.text.onAction.primary.pressed
        ],
        // Secondary Button
        [
            theme.buttonSecondaryColorBackground,
            color.background.action.secondary
        ],
        [
            theme.buttonSecondaryColorBorder,
            color.border.action.secondary
        ],
        [
            theme.buttonSecondaryColorText,
            color.text.onAction.secondary
        ],
        [
            theme.buttonSecondaryColorText,
            color.text.onAction.secondary.hovered
        ],
        [
            getColorSecondaryButtonIcon(theme),
            color.icon.onAction.secondary
        ],
        [
            getColorSecondaryButtonIcon(theme),
            color.icon.onAction.secondary.pressed
        ],
        [
            getColorSecondaryButtonIcon(theme),
            color.icon.onAction.secondary.hovered
        ],
        [
            getButtonSecondaryColorBackgroundPressed(theme),
            color.background.action.secondary.pressed
        ],
        [
            getButtonSecondaryColorBackgroundPressed(theme),
            color.background.action.secondary.hovered
        ],
        [
            getButtonSecondaryColorBorderHovered(theme),
            color.border.action.secondary.hovered
        ],
        // sail-next button text color changes on press
        // keep button text the same on press to preserve accessible button text generation
        [
            theme.buttonSecondaryColorText,
            color.text.onAction.secondary.pressed
        ],
        // Links
        [
            theme.actionPrimaryColorText,
            color.text.action.primary
        ],
        [
            getColorPrimaryLinkIcon(theme),
            color.icon.action.primary
        ],
        [
            getActionPrimaryColorTextHover(theme),
            color.text.action.primary.hovered
        ],
        [
            getColorPrimaryLinkIconHovered(theme),
            color.icon.action.primary.hovered
        ],
        [
            theme.actionSecondaryColorText,
            color.text.action.secondary
        ],
        [
            getColorSecondaryLinkIcon(theme),
            color.icon.action.secondary
        ],
        [
            LinkConfig.customize({
                uses: [
                    dynamic(({ type })=>{
                        switch(type){
                            case 'primary':
                                return [
                                    css({
                                        textDecoration: `${theme.actionPrimaryTextDecorationLine}`,
                                        textDecorationColor: theme.actionPrimaryTextDecorationColor,
                                        textDecorationStyle: theme.actionPrimaryTextDecorationStyle,
                                        textDecorationThickness: theme.actionPrimaryTextDecorationThickness,
                                        textUnderlineOffset: '1.5px',
                                        ':hover': {
                                            textDecorationColor: getActionPrimaryTextDecorationColorHovered(theme)
                                        }
                                    })
                                ];
                            case 'secondary':
                                return [
                                    css({
                                        textDecoration: `${theme.actionSecondaryTextDecorationLine}`,
                                        textDecorationStyle: theme.actionSecondaryTextDecorationStyle,
                                        textDecorationColor: theme.actionSecondaryTextDecorationColor,
                                        textDecorationThickness: theme.actionSecondaryTextDecorationThickness,
                                        ':hover': {
                                            color: getActionSecondaryColorTextHovered(theme),
                                            textDecorationStyle: theme.actionSecondaryTextDecorationStyle,
                                            textDecorationColor: getActionSecondaryTextDecorationColorHovered(theme)
                                        }
                                    })
                                ];
                            default:
                                assertUnreachable(type);
                                return [
                                    css({
                                        textDecorationStyle: theme.actionPrimaryTextDecorationStyle,
                                        textDecorationColor: theme.actionPrimaryTextDecorationColor
                                    })
                                ];
                        }
                    })
                ]
            })
        ],
        // Backdrop
        [
            BackdropConfig.customize({
                css: {
                    backgroundColor: theme.overlayBackdropColor
                }
            })
        ],
        // Toggle (selected tile background color)
        [
            ToggleItemConfig.customize({
                css: {
                    ':hover[data-checked=true]:not([data-disabled=true])': {
                        backgroundColor: 'offset'
                    }
                }
            })
        ],
        // Border Color
        [
            theme.colorBorder,
            color.border
        ],
        [
            theme.colorBorder,
            color.border.form
        ],
        [
            theme.formHighlightColorBorder,
            color.border.form.focused
        ],
        // Table rows should use the border token for their border color
        [
            BasicTableCellConfig.customize({
                css: {
                    boxShadow: 'none',
                    borderBottomWidth: 'small',
                    borderBottomStyle: 'solid',
                    borderColor: 'border'
                }
            })
        ],
        [
            BasicTableHeaderCellConfig.customize({
                css: {
                    boxShadow: 'none',
                    borderBottomWidth: 'small',
                    borderBottomStyle: 'solid',
                    borderColor: 'border'
                }
            })
        ],
        // Form
        [
            theme.formAccentColor,
            color.background.form.accent
        ],
        [
            theme.formAccentColor,
            color.border.form.accent
        ],
        [
            theme.formBackgroundColor,
            color.icon.onForm.accent
        ],
        [
            theme.formBackgroundColor,
            color.background.form.hovered
        ],
        [
            theme.formBackgroundColor,
            color.background.form.focused
        ],
        [
            theme.formBackgroundColor,
            color.background.form.pressed
        ],
        [
            getColorFormText(theme),
            color.text.form
        ],
        [
            getColorFormTextHover(theme),
            color.text.form.hovered
        ],
        [
            getColorFormTextHover(theme),
            color.text.form.focused
        ],
        [
            getColorFormTextHover(theme),
            color.text.form.pressed
        ],
        [
            getColorFormPlaceholder(theme),
            color.text.form.placeholder
        ],
        [
            getColorFormDisabledText(theme),
            color.text.form.disabled
        ],
        // disabled form icon has the same color as disabled form text: https://sail.stripe.me/beta/tokens/color/#forms
        [
            getColorFormDisabledText(theme),
            color.icon.form.disabled
        ],
        [
            getColorFormDisabledBackground(theme),
            color.background.form.disabled
        ],
        [
            getColorFormDisabledBackgroundAccent(theme),
            color.background.form.accent.disabled
        ],
        [
            getColorOnFormDisabledBackgroundAccent(theme),
            color.background.onForm.accent.disabled
        ],
        // Hide icon for form errors
        [
            FormFieldConfig.customize({
                subviews: {
                    error: {
                        uses: [
                            IconConfig.customize({
                                css: {
                                    display: 'none'
                                }
                            })
                        ]
                    }
                }
            })
        ],
        [
            FormFieldGroupConfig.customize({
                subviews: {
                    error: {
                        uses: [
                            IconConfig.customize({
                                css: {
                                    display: 'none'
                                }
                            })
                        ]
                    }
                },
                uses: [
                    ...getMobileSdkFocusRingOverride(theme, useMobileSdkTheme)
                ]
            })
        ],
        [
            PhoneNumberFieldConfig.customize({
                subviews: {
                    countryCallingCode: {
                        uses: [
                            css({
                                font: 'body.small'
                            })
                        ]
                    }
                }
            })
        ],
        [
            SwitchConfig.customize({
                uses: flattenThemingIntents([
                    // Set a static background color for the form switch element to match current switch behavior, this will be audited for CAUI-1097
                    [
                        '#FFF',
                        color.background.form
                    ],
                    // Disable hover color change for form switch element to match current switch behavior, this will be audited for CAUI-1097
                    [
                        theme.formAccentColor,
                        color.border.form.accent.hovered
                    ],
                    [
                        theme.formAccentColor,
                        color.border.form.accent.pressed
                    ]
                ])
            })
        ],
        // Text
        [
            theme.colorText,
            color.text
        ],
        [
            theme.colorText,
            color.icon
        ],
        [
            theme.colorSecondaryText,
            color.text.subdued
        ],
        [
            theme.colorSecondaryText,
            color.icon.subdued
        ],
        // Background
        [
            theme.colorBackground,
            color.background.surface
        ],
        [
            theme.offsetBackgroundColor,
            color.background.offset
        ],
        [
            theme.formBackgroundColor,
            color.background.form
        ],
        // Box Shadow
        [
            getShadowMedium(theme),
            shadow.medium
        ],
        // Feedback Colors
        [
            theme.colorDanger,
            color.text.feedback.critical
        ],
        [
            theme.colorDanger,
            color.border.form.error
        ],
        [
            theme.colorDanger,
            color.border.form.error.focused
        ],
        // destructive menu item actions
        [
            theme.colorDanger,
            color.text.action.destructive
        ],
        // Icon Colors for Default Bank/Card Icons
        [
            IconConfig.customize({
                uses: flattenThemingIntents([
                    [
                        theme.badgeNeutralColorText,
                        color.icon.onFeedback.neutral
                    ],
                    [
                        theme.badgeNeutralColorBackground,
                        color.background.feedback.neutral
                    ]
                ])
            })
        ],
        // Using body tokens for formfields instead of label
        [
            NativeTextFieldConfig.customize({
                uses: [
                    dynamic(({ size })=>{
                        switch(size){
                            case 'small':
                                return [
                                    css({
                                        font: 'body.small'
                                    })
                                ];
                            case 'medium':
                                return [
                                    css({
                                        font: 'body.small'
                                    })
                                ];
                            case 'large':
                                return [
                                    css({
                                        font: 'body.medium'
                                    })
                                ];
                            case undefined:
                                return [
                                    css({
                                        font: 'body.small'
                                    })
                                ];
                            default:
                                assertUnreachable(size);
                                return [
                                    css({
                                        font: 'body.small'
                                    })
                                ];
                        }
                    }),
                    ...getMobileSdkFocusRingOverride(theme, useMobileSdkTheme)
                ]
            })
        ],
        [
            TextAreaConfig.customize({
                subviews: {
                    input: {
                        uses: [
                            dynamic(({ size })=>{
                                switch(size){
                                    case 'small':
                                        return [
                                            css({
                                                font: 'body.small'
                                            })
                                        ];
                                    case 'medium':
                                        return [
                                            css({
                                                font: 'body.small'
                                            })
                                        ];
                                    case 'large':
                                        return [
                                            css({
                                                font: 'body.medium'
                                            })
                                        ];
                                    case undefined:
                                        return [
                                            css({
                                                font: 'body.small'
                                            })
                                        ];
                                    default:
                                        assertUnreachable(size);
                                        return [
                                            css({
                                                font: 'body.small'
                                            })
                                        ];
                                }
                            }),
                            ...getMobileSdkFocusRingOverride(theme, useMobileSdkTheme)
                        ]
                    }
                }
            })
        ],
        [
            SelectConfig.customize({
                subviews: {
                    input: {
                        uses: [
                            dynamic(({ size })=>{
                                switch(size){
                                    case 'small':
                                        return [
                                            css({
                                                font: 'body.small.emphasized'
                                            })
                                        ];
                                    case 'medium':
                                        return [
                                            css({
                                                font: 'body.small.emphasized'
                                            })
                                        ];
                                    case 'large':
                                        return [
                                            css({
                                                font: 'body.medium.emphasized'
                                            })
                                        ];
                                    case undefined:
                                        return [
                                            css({
                                                font: 'body.small.emphasized'
                                            })
                                        ];
                                    default:
                                        assertUnreachable(size);
                                        return [
                                            css({
                                                font: 'body.small.emphasized'
                                            })
                                        ];
                                }
                            })
                        ]
                    }
                }
            })
        ],
        [
            SelectFieldConfig.customize({
                subviews: {
                    button: {
                        uses: [
                            dynamic(({ size })=>{
                                switch(size){
                                    case 'small':
                                        return [
                                            css({
                                                font: 'body.small.emphasized'
                                            })
                                        ];
                                    case 'medium':
                                        return [
                                            css({
                                                font: 'body.small.emphasized'
                                            })
                                        ];
                                    case 'large':
                                        return [
                                            css({
                                                font: 'body.medium.emphasized'
                                            })
                                        ];
                                    case undefined:
                                        return [
                                            css({
                                                font: 'body.small.emphasized'
                                            })
                                        ];
                                    default:
                                        assertUnreachable(size);
                                        return [
                                            css({
                                                font: 'body.small.emphasized'
                                            })
                                        ];
                                }
                            })
                        ]
                    }
                }
            })
        ],
        // Banner Styling
        [
            BannerConfig.customize({
                css: {
                    padding: 'space.150'
                },
                uses: [
                    css({
                        wordBreak: 'break-word'
                    })
                ],
                subviews: {
                    title: {
                        uses: [
                            css({
                                font: 'label.medium.emphasized'
                            })
                        ]
                    }
                }
            })
        ],
        // Badge Colors
        [
            BadgeConfig.customize({
                // Applying the tokens here allows our overrides to be scoped only to Badge components
                uses: flattenThemingIntents([
                    [
                        theme.badgeNeutralColorText,
                        color.text.onFeedback.neutral
                    ],
                    [
                        theme.badgeNeutralColorText,
                        color.icon.onFeedback.neutral
                    ],
                    [
                        theme.badgeSuccessColorText,
                        color.text.onFeedback.success
                    ],
                    [
                        theme.badgeSuccessColorText,
                        color.icon.onFeedback.success
                    ],
                    [
                        theme.badgeWarningColorText,
                        color.text.onFeedback.attention
                    ],
                    [
                        theme.badgeWarningColorText,
                        color.icon.onFeedback.attention
                    ],
                    [
                        theme.badgeDangerColorText,
                        color.text.onFeedback.critical
                    ],
                    [
                        theme.badgeDangerColorText,
                        color.icon.onFeedback.critical
                    ],
                    [
                        theme.badgeNeutralColorBackground,
                        color.background.feedback.neutral
                    ],
                    [
                        theme.badgeSuccessColorBackground,
                        color.background.feedback.success
                    ],
                    [
                        theme.badgeWarningColorBackground,
                        color.background.feedback.attention
                    ],
                    [
                        theme.badgeDangerColorBackground,
                        color.background.feedback.critical
                    ],
                    [
                        theme.badgeNeutralColorBorder,
                        color.border.feedback.neutral
                    ],
                    [
                        theme.badgeSuccessColorBorder,
                        color.border.feedback.success
                    ],
                    [
                        theme.badgeWarningColorBorder,
                        color.border.feedback.attention
                    ],
                    [
                        theme.badgeDangerColorBorder,
                        color.border.feedback.critical
                    ]
                ]),
                css: _object_spread({}, theme.badgeBorderRadius ? {
                    borderRadius: theme.badgeBorderRadius
                } : {})
            })
        ],
        // Border Sizing
        [
            theme.borderRadius,
            border.radius.medium
        ],
        [
            theme.buttonBorderRadius,
            border.radius.action
        ],
        [
            theme.formBorderRadius,
            border.radius.form
        ],
        [
            DialogConfig.customize({
                css: {
                    // Keep with values in sync with sail-next beta Dialog
                    ['@media (max-width: 767px)']: {
                        borderRadius: '0'
                    },
                    ['@media (min-width: 768px)']: {
                        borderRadius: theme.overlayBorderRadius
                    }
                }
            })
        ],
        // Spacing
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 0.25),
            space[25]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 0.5),
            space[50]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 0.75),
            space[75]
        ],
        [
            theme.spacingUnit,
            space[100]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 1.5),
            space[150]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 2),
            space[200]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 2.5),
            space[250]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 3),
            space[300]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 3.5),
            space[350]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 4),
            space[400]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 5),
            space[500]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 6),
            space[600]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 7),
            space[700]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 8),
            space[800]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 9),
            space[900]
        ],
        [
            scaleTokenNumber(theme.spacingUnit, 'px', 10),
            space[1000]
        ],
        // Heading Typography
        // XLarge
        [
            theme.headingXlFontSize,
            font.heading.xlarge.size
        ],
        [
            theme.headingXlFontWeight,
            font.heading.xlarge.weight
        ],
        [
            theme.headingXlTextTransform,
            font.heading.xlarge.transform
        ],
        // Large
        [
            theme.headingLgFontSize,
            font.heading.large.size
        ],
        [
            theme.headingLgFontWeight,
            font.heading.large.weight
        ],
        [
            theme.headingLgTextTransform,
            font.heading.large.transform
        ],
        // Medium
        [
            theme.headingMdFontSize,
            font.heading.medium.size
        ],
        [
            theme.headingMdFontWeight,
            font.heading.medium.weight
        ],
        [
            theme.headingMdTextTransform,
            font.heading.medium.transform
        ],
        // Small
        [
            theme.headingSmFontSize,
            font.heading.small.size
        ],
        [
            theme.headingSmFontWeight,
            font.heading.small.weight
        ],
        [
            theme.headingSmTextTransform,
            font.heading.small.transform
        ],
        // XSmall
        [
            theme.headingXsFontSize,
            font.heading.xsmall.size
        ],
        [
            theme.headingXsFontWeight,
            font.heading.xsmall.weight
        ],
        [
            theme.headingXsTextTransform,
            font.heading.xsmall.transform
        ],
        // The second parameter to getLineHeight (increment) is based on the spacing scale
        [
            getLineHeight(theme === null || theme === void 0 ? void 0 : theme.headingXlFontSize, 8),
            font.heading.xlarge.lineHeight
        ],
        [
            getLineHeight(theme === null || theme === void 0 ? void 0 : theme.headingLgFontSize, 8),
            font.heading.large.lineHeight
        ],
        [
            getLineHeight(theme === null || theme === void 0 ? void 0 : theme.headingMdFontSize, 8),
            font.heading.medium.lineHeight
        ],
        [
            getLineHeight(theme === null || theme === void 0 ? void 0 : theme.headingSmFontSize, 8),
            font.heading.small.lineHeight
        ],
        [
            getLineHeight(theme === null || theme === void 0 ? void 0 : theme.headingSmFontSize, 8),
            font.heading.xsmall.lineHeight
        ],
        // Body Typography
        // Medium
        [
            theme.bodyMdFontSize,
            font.body.medium.size
        ],
        [
            theme.bodyMdFontWeight,
            font.body.medium.weight
        ],
        // Small
        [
            theme.bodySmFontSize,
            font.body.small.size
        ],
        [
            theme.bodySmFontWeight,
            font.body.small.weight
        ],
        [
            getLineHeight(theme === null || theme === void 0 ? void 0 : theme.bodyMdFontSize, 8),
            font.body.medium.lineHeight
        ],
        [
            getLineHeight(theme === null || theme === void 0 ? void 0 : theme.bodySmFontSize, 6),
            font.body.small.lineHeight
        ],
        // Label Typography
        // Medium
        [
            theme.labelMdFontSize,
            font.label.medium.size
        ],
        [
            theme.labelMdFontWeight,
            font.label.medium.weight
        ],
        [
            theme.labelMdTextTransform,
            font.label.medium.transform
        ],
        // Small
        [
            theme.labelSmFontSize,
            font.label.small.size
        ],
        [
            theme.labelSmFontWeight,
            font.label.small.weight
        ],
        [
            theme.labelSmTextTransform,
            font.label.small.transform
        ],
        [
            getLineHeight(theme === null || theme === void 0 ? void 0 : theme.labelMdFontSize, 6),
            font.label.medium.lineHeight
        ],
        [
            getLineHeight(theme === null || theme === void 0 ? void 0 : theme.labelSmFontSize, 4),
            font.label.small.lineHeight
        ],
        // Emphasized and subdued font variants have independent weights. We are right now inferring them from the regular font weights
        // Headings
        [
            calculateSubduedFontWeight(theme.headingXlFontWeight),
            font.heading.xlarge.subdued.weight
        ],
        [
            calculateSubduedFontWeight(theme.headingLgFontWeight),
            font.heading.large.subdued.weight
        ],
        [
            calculateSubduedFontWeight(theme.headingMdFontWeight),
            font.heading.medium.subdued.weight
        ],
        [
            calculateSubduedFontWeight(theme.headingSmFontWeight),
            font.heading.small.subdued.weight
        ],
        [
            calculateSubduedFontWeight(theme.headingXsFontWeight),
            font.heading.xsmall.subdued.weight
        ],
        // Body
        [
            calculateEmphasizedFontWeight(theme.bodyMdFontWeight),
            font.body.medium.emphasized.weight
        ],
        [
            calculateEmphasizedFontWeight(theme.bodySmFontWeight),
            font.body.small.emphasized.weight
        ],
        // Labels
        [
            calculateEmphasizedFontWeight(theme.labelMdFontWeight),
            font.label.medium.emphasized.weight
        ],
        [
            calculateEmphasizedFontWeight(theme.labelSmFontWeight),
            font.label.small.emphasized.weight
        ]
    ];
};
export const mapAppearanceOptionsToDesignTokens = (theme, shouldThemeForMobileSdk)=>{
    const appearanceWithDefaults = getAppearanceValuesWithDefaults(theme);
    const themingIntents = getTokenOverrideIntents(appearanceWithDefaults, shouldThemeForMobileSdk);
    return flattenThemingIntents(themingIntents);
};

//# sourceMappingURL=mapAppearanceOptionsToDesignTokens.js.map